import type { ChangeEvent } from 'react';

import { cn } from '@repo/lib/cn';
import { prettyBytes } from '@repo/lib/prettyBytes';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { toast } from 'sonner';

import type { FileInputProps } from '@/components/ui/file-multiple-upload';
import type { FileProgress, FileUploadErrorCallback } from '@/hooks/file-upload/useFileUpload.type';

import { FileInput, FileInputCard } from '@/components/ui/file-multiple-upload';
import { useFileUpload } from '@/hooks/file-upload';

export interface FilesMultipleSelectorProps extends Omit<FileInputProps, 'onAddFiles' | 'children'> {
  onFilesChange: (files: File[]) => void;
  initialFiles?: File[];
  maxSize?: number;
  maxFiles?: number;
  dropLabel?: string;
  wrapperClassName?: string;
}

const DEFAULT_MAX_SIZE = 25 * 1024 * 1024;

export function FilesMultipleSelector(props: FilesMultipleSelectorProps) {
  const { onFilesChange, initialFiles, maxSize = DEFAULT_MAX_SIZE, maxFiles, dropLabel = 'Upuść pliki tutaj', wrapperClassName, ...inputProps } = props;

  // Stabilize callbacks: useFileUpload depends on these in a useEffect,
  // so passing inline arrows would refire the effect on every render and loop.
  const handleFilesChange = useCallback((next: FileProgress[]) => onFilesChange(next.map((f) => f.file)), [onFilesChange]);

  const handleFileUploadError = useCallback<FileUploadErrorCallback<unknown>>(
    (_file, _error, { code }) => {
      if (code === 'FILE_TOO_LARGE') {
        toast.error(
          `Plik jest za duży. Maksymalny rozmiar to ${prettyBytes(maxSize, {
            locale: 'pl-PL',
            binary: true,
          })}.`
        );
      }
    },
    [maxSize]
  );

  const uploadFileFn = useCallback(async () => undefined, []);

  const [state, actions] = useFileUpload({
    maxSize,
    maxFiles,
    postponeUpload: true,
    uploadFileFn,
    onFilesChange: handleFilesChange,
    onFileUploadError: handleFileUploadError,
  });

  const dropzone = useMemo(() => actions.createDropzoneProps(), [actions]);

  // Seed restored files into the upload queue once on mount so they render after
  // a reload. We snapshot initialFiles because the parent mirrors the live file
  // list back through it via onFilesChange; reacting to later changes would
  // re-add every file the user just added and duplicate them.
  const initialFilesRef = useRef<File[] | undefined>(initialFiles);
  const seededRef = useRef<boolean>(false);
  useEffect(() => {
    if (seededRef.current || !initialFilesRef.current?.length) return;
    seededRef.current = true;
    actions.addFiles(initialFilesRef.current);
  }, [actions]);

  const handleAddFiles = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) actions.addFiles(e.target.files);
    e.target.value = '';
  };

  return (
    <div {...dropzone.props} className={cn('relative', wrapperClassName)}>
      {state.isDragging && (
        <div
          aria-hidden
          className="bg-background/25 pointer-events-none absolute inset-0 z-20 flex flex-col items-center justify-center gap-1 rounded-md border border-dashed backdrop-blur-xs select-none"
        >
          <p className="text-foreground text-base font-semibold">{dropLabel}</p>
          <p className="text-muted-foreground text-xs">Maks. {prettyBytes(maxSize, { locale: 'pl-PL', binary: true })}</p>
        </div>
      )}
      <FileInput {...inputProps} onAddFiles={handleAddFiles}>
        {state.files.map((fileObj) => (
          <FileInputCard key={fileObj.id} file={fileObj.file} onRemove={() => actions.removeFile(fileObj.id)} />
        ))}
      </FileInput>
    </div>
  );
}
